import { useEffect, useMemo, type FC } from 'react';
import { useConsentGated, type ConsentCategory } from '@fff-web/consent';
import { removeScript } from '../../core/dom';
import { AfterShipAdsContext } from './AfterShipAds.context';
import type { AfterShipAdsProviderProps } from './AfterShipAds.types';

/** OneTrust group covering targeting / advertising cookies. */
const TARGETING_CONSENT_CATEGORY: ConsentCategory = 'C0004';

export const AfterShipAdsProvider: FC<AfterShipAdsProviderProps> = ({
  children,
  appKey,
  environment,
  isEnabled = false,
  logger,
  onImpression,
  onAdClick,
}) => {
  // OneTrust resolves asynchronously and mutates when the member changes preferences, so
  // consent is state rather than a one-shot read. `isLoading` keeps an unresolved banner
  // distinct from a denial: only an actual denial should tear an already-loaded SDK out.
  const { isGranted, isLoading } = useConsentGated(TARGETING_CONSENT_CATEGORY);

  // Revoking consent mid-session must take the tag back out of the document, not just stop
  // the next request.
  useEffect(() => {
    if (isLoading || isGranted) return;
    removeScript(logger ?? console);
  }, [isGranted, isLoading, logger]);

  const contextValue = useMemo(
    () => ({
      appKey,
      environment,
      logger,
      isAllowed: Boolean(isEnabled && appKey && isGranted),
      onImpression,
      onAdClick,
    }),
    [appKey, environment, logger, isEnabled, isGranted, onImpression, onAdClick],
  );

  return (
    <AfterShipAdsContext.Provider value={contextValue}>{children}</AfterShipAdsContext.Provider>
  );
};
